import React, { useState, useEffect } from 'react';
import {
  Stack,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
  Link
} from '@mui/material';
import EditStudentDialog from '../students/EditStudentDialog'; // Import the new dialog component
import { TextField } from '@mui/material';
import { COUNTRY_CODES, getDefaultCountryCode } from '../../utils/countryCodes';
import { studentValidation } from '../students/utils/editStudentValidation';
import AddUser from '../users/AddUser';
import { useUserApis } from '../../apis/UsersAPIs.js';
import { extractPhoneParts, buildFullPhoneNumber } from '../students/utils/phoneNumberUtil.js';
import { useStudentApis } from '../../apis/StudentAPIs.js';



const CreateAccount = ({ onBackToLogin }) => {
  const { checkStudentExistsStudentIdFirstAndLastName, getStudentById, updateStudent } = useStudentApis();
  const { addUser } = useUserApis();
  const [step, setStep] = useState(1);
  const [studentId, setStudentId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [student, setStudent] = useState({
    studentId: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    studentAddress: '',
    classes: '',
    notes: '',
    activationStatus: false,
    phoneAreaCode: getDefaultCountryCode()
  });

  const [link, setLink] = useState('');

  const [errorMessage, setErrorMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [errors, setErrors] = useState({});
  
  // State for dialogs
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);

  // Handle cooldown timer
  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleStudentIDSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    
    try {
      const response = await checkStudentExistsStudentIdFirstAndLastName(studentId, firstName.trim(), lastName.trim());
      if (response.userExists) {
        alert(response.message);
      }
      else if (response.exists) {
        // Fetch the student data
        sessionStorage.setItem('jwt', response.jwtToken) // I don't believe this is needed
        const studentData = await getStudentById(studentId);
        const id = studentData._links.self.href.split('/').pop();

        const { phoneAreaCode, phoneNumber } = extractPhoneParts(studentData.phoneNumber);

        setStudent({
          ...studentData,
          phoneAreaCode,
          phoneNumber,
          studentId: id,
        });

        setLink(studentData._links.self.href);

        setStep(2);
        setEditDialogOpen(true);
      } else {
        alert('Student ID not found. Please contact your professor.');
      }
      setCooldown(30);
    } catch (err) {
      setErrorMessage(err.message || 'Unable to create account');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
      const { name, value } = event.target;
      let newValue = value;

      // Apply input restrictions
      switch (name) {
          case 'firstName':
          case 'lastName':
              newValue = value.replace(/[^a-zA-Z\s'-]/g, '');
              if (newValue.length > 50) newValue = newValue.substring(0, 50);
              break;
          case 'phoneNumber':
              newValue = value.replace(/[^\d\s()x-]/g, '');
              if (newValue.length > 20) newValue = newValue.substring(0, 20);
              break;
          case 'email':
          case 'studentAddress':
          case 'classes':
          case 'notes':
              if (newValue.length > 255) newValue = newValue.substring(0, 255);
              break;
          default:
              break;
      }

      setStudent(prev => ({ ...prev, [name]: newValue }));
      
      if (errors[name]) {
          setErrors(prev => ({ ...prev, [name]: '' }));
      }
  };
    const handlePhoneAreaCodeChange = (e) => {
        setStudent(prev => ({ ...prev, phoneAreaCode: e.target.value }));
    };

    const validateFields = () => {
        const validationErrors = {};
        
        Object.keys(studentValidation).forEach(field => {
            const rules = studentValidation[field];
            const value = student[field]?.trim() || '';
            
            if (rules.required && !value) {
                validationErrors[field] = rules.required;
            } else if (rules.pattern && !rules.pattern.value.test(value)) {
                validationErrors[field] = rules.pattern.message;
            } else if (rules.maxLength && value.length > rules.maxLength.value) {
                validationErrors[field] = rules.maxLength.message;
            } else if (rules.minLength && value.length < rules.minLength.value) {
                validationErrors[field] = rules.minLength.message;
            }
        });
        
        return validationErrors;
    };


    const handleSave = async () => {
      const validationErrors = validateFields();
      if (Object.keys(validationErrors).length > 0) {
          setErrors(validationErrors);
          return;
      }

      const fullPhoneNumber = buildFullPhoneNumber(student.phoneAreaCode, student.phoneNumber);

      // Call updateStudent and let IT handle API errors
      const updateSuccess = await updateStudent(
          { ...student, phoneNumber: fullPhoneNumber },
          link
      );

      if (updateSuccess) {
          setEditDialogOpen(false);
          setStep(3);
          setAddUserDialogOpen(true);
          return true;
      } else {
          // Only handle UI feedback if updateStudent fails
          setErrorMessage('Failed to update student information');
          setOpenSnackbar(true);
          return false;
      }
    };

  const handleCreateUser = async (username, password) => {
    if (password.length < 8) {
      setErrorMessage('Password must be at least 8 characters');
      setOpenSnackbar(true);
      return false;
    }
    
    try {
      setLoading(true);
      const result = await addUser({
        username,
        password,
        role: 'STUDENT',
        studentId: student.studentId
      });
      alert(result.success ? "Account Created" : "Something Went Wrong")
      if (result.success) {
        setStep(4);
        // Redirect to login after delay
        setTimeout(() => onBackToLogin(), 4000);
      } else {
        throw new Error('Update failed');
      }
      return true;
    } catch (err) {
      setErrorMessage('Account creation failed. Please try again.');
      setOpenSnackbar(true);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {step === 1 && (
        <form onSubmit={handleStudentIDSubmit}>
          <Stack spacing={2} alignItems="center" mt={8}>
            <Typography variant="h4" gutterBottom>Create Account</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enter your student ID. If your ID isn't found, contact your professor.
            </Typography>
            
            <TextField
              name="studentId"
              label="Student ID"
              value={studentId}
              onChange={(e) => {
                const value = e.target.value;
                // Only set the value if it's a number or empty string
                if (value === '' || /^[0-9]+$/.test(value)) {
                  setStudentId(value);
                }
              }}
              required
              error={!!errors.studentId}
              helperText={errors.studentId}
              sx={{ width: '200px' }}
              inputProps={{
                inputMode: 'numeric', // Shows numeric keyboard on mobile devices
                pattern: '[0-9]*', 
              }}
            />
                        
            <TextField
              name="firstName"
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              error={!!errors.firstName}
              helperText={errors.firstName}
              sx={{ width: '200px' }} 
            />

            <TextField
              name="lastName"
              label="LastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              error={!!errors.lastName}
              helperText={errors.lastName}
              sx={{ width: '200px' }} 
            />

            <Button 
              variant="outlined" 
              color="primary" 
              type="submit" 
              disabled={loading || cooldown > 0}
              sx={{ width: '140px' }}
            >
              {loading ? (
                <CircularProgress size={10} />
              ) : cooldown > 0 ? (
                `Check Again (${cooldown}s)`
              ) : (
                'Submit'
              )}
            </Button>
            
            <Link 
              component="button" 
              variant="body2"
              onClick={onBackToLogin}
              disabled={loading}
              sx={{ mt: 1 }}
            >
              Back to Login
            </Link>
          </Stack>
        </form>
      )}

      {/* step 2 = Edit Student Dialog */}
      <EditStudentDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setStep(1);
        }}
        student={student}
        errors={errors}
        countryCodes={COUNTRY_CODES} // Make sure to define COUNTRY_CODES or import it
        onFieldChange={handleChange}
        onPhoneAreaCodeChange={handlePhoneAreaCodeChange}
        onSave={handleSave}
      />

      {/* Add User Dialog */}
      {step === 3 && (
        <AddUser
          open={addUserDialogOpen}
          // only reset to step 1 when the user explicitly cancels
          onClose={() => {
            setAddUserDialogOpen(false);
            setStep(1);
          }}
          onSave={async (userData) => {
            const success = await handleCreateUser(
              userData.username,
              userData.password
            );
            if (success) {
              // close the dialog but leave `step` at 4
              setAddUserDialogOpen(false);
            }
            return success;
          }}
          userRole="STUDENT"
          newUserData={{
            username: student.email,
            password: '',
            role: 'STUDENT',
            studentId: student.studentId
          }}
          skipPermissionCheck={true}
        />
      )}


      {step === 4 && (
        <Stack spacing={2} alignItems="center" mt={8}>
          <Typography variant="h4" gutterBottom>Account Created!</Typography>
          <Typography variant="body1" color="text.secondary" mb={2}>
            Your account has been successfully created.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You'll be redirected to the login page shortly...
          </Typography>
          <CircularProgress sx={{ mt: 2 }} />
        </Stack>
      )}
      
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        message={errorMessage}
      />
    </div>
  );
};

export default CreateAccount;